import React, { useId } from 'react';

/**
 * iVisora wordmark logo.
 *
 * Props:
 *   variant   "gradient" (default, brand purple→pink) | "white" | "mono" (currentColor)
 *   className Tailwind/CSS classes; control size with h-* (width auto-scales)
 *   title     Accessible name (default "iVisora")
 *   glow      true → adds a soft purple drop-shadow on hover (used in nav)
 */
const STOPS = [
  { offset: '0%',  color: '#7B61FF' },
  { offset: '55%', color: '#C93CFF' },
  { offset: '100%', color: '#FF4FD8' },
];

const IvisoraLogo = ({
  variant = 'gradient',
  className = 'h-9',
  title = 'iVisora',
  glow = false,
}) => {
  const uid = useId().replace(/:/g, '');
  const gradId = `ivisora-grad-${uid}`;

  const fill =
    variant === 'gradient' ? `url(#${gradId})` :
    variant === 'white'    ? '#FFFFFF' :
    'currentColor';

  const hoverClasses = glow
    ? 'transition-[opacity,filter] duration-200 hover:opacity-95 hover:[filter:drop-shadow(0_4px_18px_rgba(201,60,255,0.35))]'
    : 'transition-opacity duration-200 hover:opacity-90';

  return (
    <svg
      role="img"
      aria-label={title}
      viewBox="0 0 1320 261"
      xmlns="http://www.w3.org/2000/svg"
      className={`block w-auto select-none ${hoverClasses} ${className}`}
      shapeRendering="geometricPrecision"
    >
      {variant === 'gradient' && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            {STOPS.map(s => <stop key={s.offset} offset={s.offset} stopColor={s.color} />)}
          </linearGradient>
        </defs>
      )}
      <g fill={fill}>
        {/* i (left) — dot + stem */}
        <circle cx="22.84" cy="22.84" r="22.84" />
        <path d="M45.0603 236.45C45.0903 251.09 35.7403 260.41 21.7103 259.98C8.99031 259.59 0.260308 251 0.280308 237.9L0.510308 81.1899C0.530308 68.8399 10.1303 61.4099 21.7103 61.0499C33.7903 60.6699 44.6903 68.1999 44.7203 81.1899L45.0503 236.45H45.0603Z" />
        {/* V */}
        <path d="M374.09 0.279902L398.67 0.159902C402.44 0.139902 405.86 2.6699 407.35 5.0799C409.36 8.3199 409.16 12.2999 407.21 15.5999L375.9 68.4099L273.99 239.01C265.7 252.88 253.17 261.27 236.65 260.09C222.83 259.11 212.7 251.8 205.65 239.89L115.77 88.0999L72.77 15.1699C70.81 11.8399 70.76 8.1899 72.42 5.2099C73.82 2.6899 77.2601 0.189902 80.8001 0.179902L104.85 0.149902C112.38 0.149902 120.21 3.8399 124.18 10.7499L196.06 135.67L236.63 207.04C237.33 208.27 239.2 208.23 240.11 208.12C241.72 207.93 242.1 207.14 243.16 205.32L312.64 87.0799L357.71 10.0799C360.89 4.6399 367.6 0.329902 374.09 0.299902V0.279902Z" />
        {/* i (mid) — dot + stem */}
        <circle cx="450.25" cy="22.88" r="22.88" />
        <path d="M471.9 241.53C471.88 253.47 461.03 259.55 450.67 259.97C440.12 260.41 427.84 253.93 427.82 241.94L427.66 81.15C427.65 68.4 439.1 60.84 450.6 61.14C463.02 61.47 472.16 69.46 472.14 82.19L471.89 241.52L471.9 241.53Z" />
        {/* s */}
        <path d="M687.03 239.96C674.61 253.4 657.72 259.05 639.65 259.01L516.86 258.68C506.59 258.65 501.36 248.25 501.52 239.11C501.69 229.14 507.29 219.62 518.05 219.59L639.55 219.18C652.59 219.14 660.92 208.34 659.72 196.11C658.72 185.92 651.25 177.36 640.36 177.31L562.8 177C550.59 176.95 539.03 175.17 528.69 169.25C510.79 158.99 501.74 141.52 501.51 120.93C501.17 90.32 523.91 62.41 555.95 62.38L681.56 62.27C691.1 62.27 695.22 73.42 695.42 80.9C695.67 90.37 690.76 100.45 680.47 100.49L561.9 100.94C553.72 100.97 547.26 106.35 544.68 112.58C541.77 119.62 542.9 127.86 548.63 133.15C552.94 137.14 558.88 138.36 564.79 138.35L641.72 138.21C650.47 138.19 658.57 140.3 666.34 143.35C688.23 151.94 700.89 171.78 701.7 195.03C702.28 211.64 698.82 227.21 687.04 239.96H687.03Z" />
        {/* o */}
        <path d="M877.85 258.32L802.73 258.94C760.69 259.29 728.86 228.13 728.9 187.1L728.95 132.95C728.98 95.15 759.51 63.35 797.75 62.24L872.66 62.13C911.55 62.07 941.87 93.69 943.29 132.24L943.46 185.07C943.59 223.42 916.11 255.96 877.84 258.32H877.85ZM868.4 219.25C886.71 219.29 902.51 205.01 902.64 186.98L903.03 133.1C900.91 115.35 887.94 100.92 869.67 100.88L799.65 100.7C783.21 103.02 769.77 116.09 769.69 133.15L769.47 186.07C769.39 203.87 783.8 219.06 801.74 219.1L868.4 219.26V219.25Z" />
        {/* r */}
        <path d="M1041.23 100.9C1025.97 102.89 1014.57 113.83 1014.5 129.18L1014.02 241.36C1013.97 252.42 1004.29 259.79 993.67 259.87C983.96 259.94 973.42 252.95 973.41 242.44L973.26 128.35C973.21 92.6298 1001.28 62.0698 1034.72 62.1398L1101.71 62.2798C1111.93 62.2998 1116.91 72.9298 1116.65 81.9898C1116.4 90.5898 1111.81 100.28 1102.32 100.36L1041.23 100.91V100.9Z" />
        {/* a */}
        <path d="M1269.9 157.61C1269.66 168.22 1263.12 176.61 1252.56 176.68L1180.94 177.18C1168.2 177.27 1160.4 189.05 1161.94 200.86C1163.2 210.47 1170.21 218.98 1181.03 219L1267.49 219.15C1274.87 219.16 1279.59 212.53 1279.57 205.95L1279.36 127.32C1279.32 113.53 1269.02 101.17 1254.58 101.08L1151.06 100.43C1140.84 100.37 1136.06 90.88 1136.08 81.55C1136.1 73.16 1140.67 62.1799 1150.77 62.1899L1259.79 62.2999C1291.65 62.3299 1317.84 89.16 1319.46 121.14L1319.78 214.12C1319.86 237.2 1301 258.27 1277.53 258.32L1175.85 258.53C1154.14 258.57 1135.26 244.45 1126.61 225.45C1121.68 214.62 1120.7 203.7 1121.43 192.04C1123.46 159.64 1148.18 138.23 1180.77 138.19L1250.67 138.1C1262.02 138.09 1270.17 145.65 1269.9 157.61Z" />
      </g>
    </svg>
  );
};

export default IvisoraLogo;
